import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { Address } from "@app/address/model/address";
import { AddressService } from "@app/address/store/services/address.service";
import { CellularEditComponent, CellularEditConfig } from "@app/cellular/components/cellular-edit/cellular-edit.component";
import { FormSubmissionError, FormSubmissionStatus } from "@app/cellular/components/cellular-info/cellular-info.component";
import { CellularDevice, CellularProviderStatuses } from "@app/cellular/models/cellular-device.model";
import { CellularService } from "@app/cellular/services/cellular.service";
import { CHSecurityDevice, Project, SecuritySystem } from "@app/project/model";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { DevicesActions, ProjectActions } from "@app/project/store";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { LoadingState } from "@app/root-store/api-result.state";
import { CellularProviderType } from "@app/shared/databases/cellular.database.service";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import {
    EMPTY,
    Observable,
    Subject,
    catchError,
    defer,
    exhaustMap,
    filter,
    finalize,
    first,
    interval,
    map,
    of,
    repeat,
    retry,
    skip,
    switchMap,
    take,
    takeUntil,
    tap,
    throwError,
    timer,
} from "rxjs";

enum EnableLteStatus {
    COMPLETE = "COMPLETE",
    INCOMPLETE = "INCOMPLETE",
    UNKNOWN = "UNKNOWN",
}

enum CellularAction {
    ENABLE_LTE = "Enable LTE",
    UPDATE_PANEL_PROVIDER = "Send Provider Update to Panel",
    SAVE = "Save",
}

@Component({
    selector: "clare-enable-lte-step",
    templateUrl: "./enable-lte-step.component.html",
    styleUrls: ["./enable-lte-step.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnableLteStepComponent implements OnInit, OnDestroy, OnChanges {
    @Input() cellularDevice?: CellularDevice;
    @Input() chSecurityDevice$: Observable<CHSecurityDevice | undefined>;
    @Input() isComplete!: boolean; // Whether this step was previously completed
    @Input() isLteDisabled!: boolean | null;
    @Input() project: Project;
    @Input() skip!: boolean;
    @Input() uuid: string;

    @ViewChild(CellularEditComponent) cellularEdit?: CellularEditComponent;

    address$: Observable<Address>;
    dbSecurityDevice: SecuritySystem;
    cellularEditConfig: Partial<CellularEditConfig> = {
        fullWidth: true,
        ignoreTitleCase: true,
        leftAlignSpinner: true,
        hideSubmit: true,
        hideTitle: true,
        isInWizard: true,
    };
    cellularProviderAction: CellularAction = CellularAction.ENABLE_LTE;
    providerOnPanel: CellularProviderType | null = null;
    chSecurityDevice: CHSecurityDevice | undefined;
    private chSecurityDeviceSub = EMPTY.subscribe();
    private projectOnlineSub = EMPTY.subscribe();
    submissionErrors: Partial<Record<CellularProviderType, FormSubmissionError>> = {};
    submissionStatus: FormSubmissionStatus = { inProgress: false, text: "" };
    unsubscribe$ = new Subject<void>();

    @Output() done = new EventEmitter();
    @Output() updateFailed = new EventEmitter();
    @Output() fetchCellularDevice = new EventEmitter();
    @Output() fetchChSecurityDevice = new EventEmitter();
    @Output() enableCellularTriggering = new EventEmitter();
    @Output() processingActivate = new EventEmitter();

    constructor(
        private readonly addressService: AddressService,
        private readonly cdr: ChangeDetectorRef,
        private readonly cellularService: CellularService,
        private readonly deviceDispatchers: DeviceDispatchers
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.chSecurityDevice$) {
            // Extract chSecurityDevice and panel provider
            this.chSecurityDeviceSub.unsubscribe();
            this.chSecurityDeviceSub = this.chSecurityDevice$
                .pipe(
                    tap(chSecurityDevice => {
                        this.chSecurityDevice = chSecurityDevice;
                        this.cdr.detectChanges();
                    }),
                    switchMap(chSecurityDevice => this.fetchCellProvider$(chSecurityDevice?.ChildUIDs?.[0] || null)),
                    tap((panelProvider: CellularProviderType | null) => {
                        this.providerOnPanel = panelProvider;
                        this.onUpdateProviderAction();
                    }),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe();

            // If panel offline, poll until online (so we can re-enable the send-provider-update buttons)
            this.projectOnlineSub.unsubscribe();
            this.projectOnlineSub = this.chSecurityDevice$
                .pipe(
                    take(1),
                    tap(chSecurityDevice => {
                        if (!chSecurityDevice && !this.submissionStatus.inProgress && this.lteStatus === EnableLteStatus.INCOMPLETE) {
                            this.fetchChSecurityDevice.emit();
                        }
                    }),
                    repeat({
                        delay: 5000,
                    }),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe();
        }

        if (changes.project) {
            this.dbSecurityDevice = ProjectHelperService.getDbSecuritySystem(this.project);
        }
    }

    ngOnInit(): void {
        this.address$ = this.addressService.getAddressById$(this.project?.addressId).pipe(
            filter(address => !!address),
            take(1),
            catchError(e => throwError(() => e))
        );

        this.fetchCellularDevice.emit();
        interval(5000)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (this.isLteDisabled === null) {
                    this.fetchCellularDevice.emit();
                }
            });
    }

    updateProvider(provider: string, chSecurityDevice: CHSecurityDevice | undefined, hideErrorToast = false) {
        this.submissionStatus = { inProgress: true, text: "Updating panel" };
        this.updateProvider$(provider, chSecurityDevice, hideErrorToast)
            .pipe(
                tap(() => {
                    delete this.submissionErrors[provider.toLocaleLowerCase()];
                }),
                finalize(() => {
                    this.submissionStatus = { inProgress: false, text: "" };
                })
            )
            .subscribe();
    }

    private updateProvider$(provider: string, chSecurityDevice: CHSecurityDevice | undefined, hideErrorToast = false): Observable<null> {
        const emitter = new EventEmitter();

        return of(null).pipe(
            // Check if panel is online
            switchMap(() => {
                if (!this.chSecurityDevice) {
                    this.fetchChSecurityDevice.emit();
                    return this.chSecurityDevice$.pipe(skip(1));
                }

                return of(this.chSecurityDevice);
            }),
            // Load DCO properties before trying to update them
            switchMap(chSecurityDevice => {
                const partitionUid = chSecurityDevice?.ChildUIDs?.[0] || null;
                if (!partitionUid) {
                    return of(chSecurityDevice);
                }

                return this.deviceDispatchers.selectDeviceClassObjectState(partitionUid, DEVICE_CLASSES.CELLULAR_QUALITY_DCO).pipe(
                    tap(state => {
                        if (state === LoadingState.init) {
                            this.deviceDispatchers.getDeviceClassObject({
                                uuid: ProjectHelperService.getProjectUuid(this.project),
                                deviceUid: partitionUid,
                                dco: DEVICE_CLASSES.CELLULAR_QUALITY_DCO,
                            });
                        }
                    }),
                    filter(state => state === LoadingState.loaded),
                    take(1),
                    map(() => chSecurityDevice)
                );
            }),
            // Update panel provider
            switchMap(chSecurityDevice => {
                const partitionUid = chSecurityDevice?.ChildUIDs?.[0] || null;
                this.deviceDispatchers.dispatchUpdateDeviceClassObject({
                    uuid: ProjectHelperService.getProjectUuid(this.project),
                    deviceUid: partitionUid,
                    deviceClass: DEVICE_CLASSES.CELLULAR_QUALITY_DCO,
                    property: "carrier",
                    args: provider.toUpperCase(),
                    hideErrorToast,
                    callbackEmitter: emitter,
                });
                return emitter;
            }),
            // Throw error if panel offline
            map(action => {
                if (action.type === DevicesActions.ActionTypes.UpdateDeviceClassObjectFailure) {
                    let msg = action.error.error;
                    if (msg.includes("EOF")) {
                        msg = "The controller is not connected to the ClareCloud server.";
                    }
                    throw new Error(msg);
                } else {
                    return null;
                }
            }),
            // Update isCompleted in db
            exhaustMap(() => {
                const update = { ...this.dbSecurityDevice, isCompleted: true };
                return this.cellularService.saveCellularDeviceToDb(this.project, update);
            }),
            // Refresh cell status
            tap(() => {
                this.fetchCellularDevice.emit();
            }),
            first()
        );
    }

    onCellularAction({ action, cellularForm }): void {
        switch (action) {
            case CellularAction.ENABLE_LTE:
                this.enableCellularTriggering.emit(cellularForm);
                this.onActivateCellularDevice(cellularForm);
                return;
            case CellularAction.UPDATE_PANEL_PROVIDER:
                this.updateProvider(cellularForm.provider, this.chSecurityDevice);
                return;
            case CellularAction.SAVE:
                this.onSaveCellularDevice(cellularForm);
                return;
            default:
                console.error("Invalid action", action);
        }
    }

    /**
     * When user selects new provider (or a default is set), update the submit button
     *
     * @param providerSelectedOverride
     */
    onUpdateProviderAction(providerSelectedOverride?: CellularProviderType): void {
        if (this.submissionStatus.inProgress) {
            return;
        }

        const providerSelected = providerSelectedOverride || this.cellularEdit?.cellularForm.value.provider;
        const deviceStatus = this.cellularService.getCellularDeviceStatus(this.cellularDevice);
        const statusUnknown =
            deviceStatus === CellularProviderStatuses.Unknown && this.cellularDevice?.provider !== CellularProviderType.NONE;

        if (
            !providerSelected ||
            providerSelected === CellularProviderType.NONE ||
            this.cellularService.isBmobile(providerSelected) ||
            statusUnknown
        ) {
            this.cellularProviderAction = CellularAction.SAVE;
        } else if (this.isActivationPartiallyComplete) {
            // LTE is activated but LTE config script on panel hasn't run
            this.cellularProviderAction = CellularAction.UPDATE_PANEL_PROVIDER;
        } else {
            this.cellularProviderAction = CellularAction.ENABLE_LTE;
        }

        this.cdr.detectChanges();
    }

    private fetchCellProvider$(partitionUID: string): Observable<CellularProviderType> {
        return partitionUID
            ? this.deviceDispatchers
                  .selectDeviceClassObjectPropertyValue(partitionUID, DEVICE_CLASSES.CELLULAR_QUALITY_DCO, "carrier")
                  .pipe(
                      map(provider => {
                          const prov: CellularProviderType = CellularProviderType[provider];
                          return prov;
                      })
                  )
            : of(null);
    }

    private onActivateCellularDevice({
        iccid,
        imei,
        macAddress,
        provider,
        mdnZipCode,
    }: {
        iccid: string;
        imei: string;
        macAddress: string;
        provider: CellularProviderType;
        mdnZipCode: string;
    }): void {
        this.submissionStatus = { inProgress: true, text: "" };
        this.dbSecurityDevice.isCompleted = false;
        delete this.submissionErrors[provider.toLocaleLowerCase()];

        const checkStatus$ = defer(() => {
            this.updateSubmissionStatus({ text: "Checking status" });
            return this.cellularService.fetchCellularDevice(iccid, provider).pipe(
                catchError(e => {
                    console.error(e);
                    const msg = `There was a problem contacting the cellular provider
            The ICCID: ${iccid} is not registered with the selected provider.`;
                    return throwError(() => new Error(msg));
                })
            );
        });
        const saveToDb$ = defer(() => {
            this.updateSubmissionStatus({ text: "Saving" });
            return this.saveCellularDevice$({ iccid, imei, macAddress, provider, isCompleted: false }).pipe(
                map(action => {
                    if (action.type === ProjectActions.ActionTypes.UpdateProjectSuccess) {
                        this.dbSecurityDevice = action.payload.securitysystem;
                        return action;
                    }

                    console.error(action);
                    throw new Error("Failed to save cellular device");
                })
            );
        });
        const activate$ = defer(() => {
            this.updateSubmissionStatus({ text: "Activating LTE" });
            const securityDevice = { ...this.dbSecurityDevice, iccid, cellularProvider: provider, imei, macAddress, isCompleted: false };
            return this.cellularService.activateCellularDevice(securityDevice, mdnZipCode, this.project, provider).pipe(
                tap((result: any) => {
                    this.processingActivate.emit();
                    if (!result) {
                        throw new Error(result);
                    }
                })
            );
        });
        const waitForActive$ = defer(() => {
            const start = Date.now();

            return timer(1000, 4000).pipe(
                exhaustMap(() => this.cellularService.fetchCellularDevice(iccid, provider)),
                tap(device => {
                    this.updateSubmissionStatus({ text: `LTE is ${device.status}` });
                    const elapsed = Date.now() - start;
                    this.processingActivate.emit();
                    if (elapsed >= 120 * 1000) {
                        this.updateSubmissionStatus({
                            altText:
                                "Activation is taking longer than expected. You may visit the Cellular Provider section under Service Plans at a later date to complete the activation process.",
                        });
                    }
                }),
                filter(device => device.status === CellularProviderStatuses.Active),
                take(1)
            );
        });
        const updatePanelProvider$ = defer(() => {
            this.updateSubmissionStatus({ text: "Updating panel" });
            return this.updateProvider$(provider, this.chSecurityDevice, true);
        }).pipe(
            // Wait a few seconds to see if panel comes online
            retry({
                count: 3,
                delay: e => {
                    console.error(e);
                    return timer(5000).pipe(switchMap(() => of(null)));
                },
            })
        );

        let didActivate = false;
        of(null)
            .pipe(
                exhaustMap(() => checkStatus$),
                exhaustMap(() => saveToDb$),
                exhaustMap(() => activate$),
                tap(() => (didActivate = true)),
                exhaustMap(() => waitForActive$),
                exhaustMap(() => updatePanelProvider$),
                first(),
                tap(() => {
                    this.done.emit();
                }),
                catchError(e => {
                    console.error(e);

                    // Set error message
                    if (didActivate) {
                        // Regular error || raw API error || DataServiceError || any
                        const msg = `Activation incomplete. ${e.message || e.error?.message || e.details || e}`;
                        this.submissionErrors[provider.toLocaleLowerCase()] = { text: msg, isUpdateProviderError: true };
                    } else {
                        const msg = `Activation failed. ${e.message || e.error?.message || e.details || e}`;
                        this.submissionErrors[provider.toLocaleLowerCase()] = { text: msg };
                    }
                    this.cdr.detectChanges();
                    this.updateFailed.emit();
                    this.onUpdateProviderAction();
                    return throwError(() => e);
                }),
                finalize(() => {
                    this.fetchCellularDevice.emit();
                    this.submissionStatus = { inProgress: false, text: "" };
                    this.cdr.detectChanges();
                })
            )
            .subscribe();
    }

    private onSaveCellularDevice({ iccid, imei, macAddress, provider }) {
        this.submissionStatus = { inProgress: true, text: "" };

        this.saveCellularDevice$({ iccid, imei, macAddress, provider, isCompleted: true })
            .pipe(
                tap(() => {
                    if (provider === CellularProviderType.BMOBILE) {
                        // bmobile doesn't need to be activated so mark step as completed
                        this.done.emit();
                    }

                    this.fetchCellularDevice.emit();
                }),
                catchError(() => {
                    this.onUpdateProviderAction();

                    this.cdr.detectChanges();
                    return of(null);
                }),
                finalize(() => {
                    this.submissionStatus = { inProgress: false, text: "" };
                })
            )
            .subscribe();
    }

    private updateSubmissionStatus(overrides = {}): void {
        this.submissionStatus = {
            ...this.submissionStatus,
            ...overrides,
        };
        this.cdr.detectChanges();
    }

    /**
     * Save cell info to db
     *
     * @param root0
     * @param root0.iccid
     * @param root0.imei
     * @param root0.macAddress
     * @param root0.provider
     * @param root0.isCompleted
     */
    private saveCellularDevice$({
        iccid,
        imei,
        macAddress,
        provider,
        isCompleted,
    }): Observable<ProjectActions.UpdateProjectSuccess | ProjectActions.UpdateProjectFailure> {
        const cellularProvider = !provider || provider === CellularProviderType.NONE ? CellularProviderType.NONE : provider;
        const securityDevice = { ...this.dbSecurityDevice, iccid, cellularProvider, imei, macAddress, isCompleted };

        const save$ = defer(() => this.cellularService.saveCellularDeviceToDb(this.project, securityDevice));
        return save$.pipe(take(1));
    }

    get lteStatus(): EnableLteStatus {
        // Form was previously submitted
        if (this.isComplete) {
            return EnableLteStatus.COMPLETE;
        }

        // Still fetching cellular device
        if (this.isLteDisabled === null) {
            return EnableLteStatus.UNKNOWN;
        }

        return this.isActivationFullyComplete ? EnableLteStatus.COMPLETE : EnableLteStatus.INCOMPLETE;
    }

    get defaultProviderOverride(): CellularProviderType {
        // If LTE is enabled, the form should be locked to that
        if (this.isLteDisabled === false) {
            return this.cellularDevice.provider;
        }

        // Otherwise, if panel is online, use panel provider
        if (this.providerOnPanel) {
            return this.providerOnPanel;
        }

        return CellularProviderType.NONE;
    }

    get isActivationFullyComplete(): boolean {
        const isLteEnabled = this.isLteDisabled === false;
        const isPanelConfigured = !!this.project.securitysystem?.isCompleted;
        return isLteEnabled && isPanelConfigured;
    }

    get isActivationPartiallyComplete(): boolean {
        const isLteEnabled = this.isLteDisabled === false;
        const isPanelConfigured = !!this.project.securitysystem?.isCompleted;
        return isLteEnabled && !isPanelConfigured;
    }

    ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
